import { forwardRef, useState } from 'react';
import { type FieldError } from 'react-hook-form';

import { ReactComponent as HideIcon } from 'assets/icons/1xbet/close-eye-icon.svg';
import { ReactComponent as CrossIcon } from 'assets/icons/1xbet/cross-icon.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/1xbet/info-icon.svg';
import { ReactComponent as ShowIcon } from 'assets/icons/1xbet/open-eye-icon.svg';
import { twMergeClsx } from 'utils/tw-with-clsx';
import { InputType, type InputRefType, type InputTypePropTypes } from 'views/shared/input-type';
import { Tooltip } from 'views/shared/tooltip';

interface InputPropTypes {
  error?: boolean | string | null | FieldError;
  label?: string;
  containerClassName?: string;
  LeftIcon?: JSX.Element | null;
  RightIcon?: JSX.Element | null;
  clear?: () => void;
  info?: string;
  showPass?: boolean;
}

const inputStyles =
  'px-4 py-[6px] md:py-3 bg-white outline-none h-[42px] md:h-[50px] rounded-xsmall text-base md:text-xl text-black leading-[30px] border border-solid border-black';

const Input = forwardRef<InputRefType, InputPropTypes & InputTypePropTypes>((props, ref) => {
  const {
    inputClassName,
    amount,
    error,
    containerClassName,
    label,
    name,
    id,
    value,
    disabled,
    LeftIcon,
    RightIcon,
    info,
    clear,
    showPass,
    type,
  } = props;
  const [currentType, setCurrentType] = useState(type);

  const inputClassess = twMergeClsx(
    inputStyles,
    inputClassName,
    amount && '',
    error && 'text-betCity-red border-betCity-red',
    disabled && 'cursor-not-allowed',
    LeftIcon && 'pl-[50px]',
    (RightIcon || (showPass && value)) && 'pr-10 md:pr-[50px]',
  );

  const changeInputType = () => {
    setCurrentType(currentType === 'password' ? 'tel' : 'password');
  };

  return (
    <div className={twMergeClsx('text-sz-gray-300', containerClassName)}>
      {label ? (
        <label
          className={twMergeClsx(
            'text-sm leading-[14px] font-normal mb-2 block whitespace-nowrap',
            error && 'text-sz-red',
          )}
          htmlFor={name || id}
        >
          {label}
        </label>
      ) : null}
      <div className="flex items-center gap-2">
        <div className="relative w-full">
          {LeftIcon && (
            <div className="absolute left-4 inset-y-0 flex items-center justify-center">
              {LeftIcon}
            </div>
          )}
          <InputType ref={ref} {...props} type={currentType} inputClassName={inputClassess} />
          <div className="absolute right-4 inset-y-0 flex items-center justify-center">
            {amount && <span className="text-base font-semibold text-black font-sans">₸</span>}
            {value && clear && (
              <button className="w-4 md:w-6 h-4 md:h-6" type="button" onClick={clear}>
                <CrossIcon className="fill-1xbet-black" />
              </button>
            )}
            {value && showPass && (
              <button className="w-4 md:w-6 h-4 md:h-6" type="button" onClick={changeInputType}>
                {currentType === 'password' ? (
                  <ShowIcon className="w-4 md:w-6 h-4 md:h-6" />
                ) : (
                  <HideIcon className="w-4 md:w-6 h-4 md:h-6" />
                )}
              </button>
            )}
            {RightIcon}
          </div>
        </div>
        {info && (
          <div className="w-4 md:w-6 h-4 md:h-6">
            <Tooltip
              text={info}
              className="bg-white border border-black rounded-xsmall px-3 py-[6px] whitespace-pre mb-2"
            >
              <InfoIcon className="w-4 md:w-5 h-4 md:h-5" />
            </Tooltip>
          </div>
        )}
      </div>

      {typeof value === 'string' && error && typeof error === 'string' && (
        <div className="text-betCity-red text-[10px] leading-3 text-left mt-[1px] lowercase whitespace-nowrap absolute">
          {error}
        </div>
      )}
    </div>
  );
});

export { Input };
